<template>
  <div class="avg-comparison-widget">
    <v-chart :option="option" autoresize class="bar-chart" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  TitleComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatNumber, formatWeekday } from '@/i18n/format'

use([
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  CanvasRenderer
])

const props = defineProps({
  avgAll: { type: Number, required: true },
  avgSameWeekday: { type: Number, required: true },
  zoneColor: { type: String, default: '#4F7EB3' },
  timeSpan: { type: String, required: true },
  currentWeekday: { type: Number, required: true },
})

// map 0–6 → weekday names
const weekdayNames = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]

const { t } = useI18n()

// fallback to raw prop if out-of-range. Dutch: "maandag" (1 Jan 2024 was a Monday).
const currentWeekdayLabel = computed(() =>
  props.currentWeekday >= 0 && props.currentWeekday < 7
    ? (currentLanguage() === 'en'
        ? weekdayNames[props.currentWeekday]
        : formatWeekday(new Date(2024, 0, 1 + props.currentWeekday)))
    : String(props.currentWeekday)
)

function colorWithAlpha(color, alpha) {
  color = color.trim()
  if (color.startsWith('rgba')) {
    return color.replace(/rgba\(([\d\s,]+)\)/, `rgba($1,${alpha})`)
  }
  if (color.startsWith('rgb(')) {
    const [r, g, b] = color.match(/\d+/g)
    return `rgba(${r},${g},${b},${alpha})`
  }
  let hex = color.replace(/^#/, '')
  if (hex.length === 3) hex = hex.split('').map(h => h + h).join('')
  const intVal = parseInt(hex, 16)
  const r = (intVal >> 16) & 0xff
  const g = (intVal >> 8) & 0xff
  const b = intVal & 0xff
  return `rgba(${r},${g},${b},${alpha})`
}

const isEn = () => currentLanguage() === 'en'

const option = computed(() => ({
  title: {
    text: t('charts.comparison.title', { span: props.timeSpan }),
    left: 'center',
    textStyle: { color: props.zoneColor, fontWeight: 'lighter' }
  },
  // Dutch numbers read 1.234; English keeps ECharts' own '{c}'.
  tooltip: { trigger: 'axis', formatter: isEn() ? '{b}: {c}' : ps => ps.map(p => `${p.name}: ${formatNumber(p.value)}`).join('<br>') },
  grid: { left: 170, right: 60, bottom: 70, top: 30 },
  xAxis: {
    type: 'value',
    min: 0,
    axisLine: { lineStyle: { color: '#ccc' } },
    axisLabel: { formatter: isEn() ? '{value}' : v => formatNumber(v) }
  },
  yAxis: {
    type: 'category',
    data: [t('charts.comparison.onlyOn', { day: currentWeekdayLabel.value }), t('charts.comparison.allDays')],
    axisLine: { lineStyle: { color: '#ccc' } },
    axisTick: { show: false }
  },
  series: [
    {
      type: 'bar',
      data: [Math.round(props.avgSameWeekday), Math.round(props.avgAll)],
      itemStyle: {
        color: params => {
          // lighter color for the “All days” bar
          return params.dataIndex === 1
            ? props.zoneColor
            : colorWithAlpha(props.zoneColor, 0.4)
        }
      },
      barWidth: 40,
      label: {
        show: true,
        position: 'right',
        formatter: isEn() ? '{c}' : p => formatNumber(p.value),
        fontSize: 14,
        color: params => {
          // lighter color for the “All days” bar
          return params.dataIndex === 1
            ? props.zoneColor
            : colorWithAlpha(props.zoneColor, 0.4)
        }
      }
    }
  ]
}))
</script>

<style scoped>
.bar-chart {
  width: 100%;
  height: 200px;
}
</style>